import { useState } from "react";
import { TypeAddInput } from "./TypeAddInput";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { X, MapPin, Globe, Check } from "lucide-react";
import { ManagedCampaignData } from "./ManagedCampaignData";

interface ManagedGeographyStepProps {
  data: ManagedCampaignData;
  updateData: (updates: Partial<ManagedCampaignData>) => void;
}

const POPULAR_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "UAE",
  "Singapore",
];

const CITY_SUGGESTIONS: Record<string, string[]> = {
  "United States": ["New York", "San Francisco", "Austin", "Chicago", "Boston"],
  "United Kingdom": ["London", "Manchester", "Birmingham", "Edinburgh", "Leeds"],
  "India": ["Bangalore", "Mumbai", "Delhi", "Pune", "Hyderabad"],
  "Canada": ["Toronto", "Vancouver", "Montreal", "Ottawa", "Calgary"],
  "Australia": ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide"],
  "Germany": ["Berlin", "Munich", "Hamburg", "Frankfurt", "Stuttgart"],
  "UAE": ["Dubai", "Abu Dhabi", "Sharjah"],
  "Singapore": ["Singapore"],
};

export const ManagedGeographyStep = ({ data, updateData }: ManagedGeographyStepProps) => {
  const [countryInput, setCountryInput] = useState("");
  const [cityInput, setCityInput] = useState("");

  const toggleCountry = (country: string) => {
    if (data.countries.includes(country)) {
      updateData({ countries: data.countries.filter(c => c !== country) });
    } else {
      updateData({ countries: [...data.countries, country] });
    }
  };

  const addCountry = () => {
    const country = countryInput.trim();
    if (country && !data.countries.includes(country)) {
      updateData({ countries: [...data.countries, country] });
      setCountryInput("");
    }
  };

  const handleCountryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCountry();
    }
  };

  const addCity = (cityOverride?: string) => {
    const city = (cityOverride || cityInput).trim();
    if (city && !data.cities.includes(city)) {
      updateData({ cities: [...data.cities, city] });
      setCityInput("");
    }
  };

  const removeCity = (city: string) => {
    updateData({ cities: data.cities.filter(c => c !== city) });
  };

  const handleCityKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCity();
    }
  };

  // Suggest cities from selected countries, or default to India if none selected
  const suggestedCities = (data.countries.length > 0 ? data.countries : ["India"])
    .flatMap(country => CITY_SUGGESTIONS[country] || [])
    .filter(city => !data.cities.includes(city));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Countries */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Select Countries <span className="text-destructive">*</span>
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Choose the countries where you want to find prospects
            </p>

            {/* Selected Countries Chips */}
            {data.countries.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {data.countries.map(country => (
                  <Badge
                    key={country}
                    variant="default"
                    className="gap-1 pl-2.5 pr-2 py-1.5 bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm"
                  >
                    <Check className="w-3 h-3" />{country}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => toggleCountry(country)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <TypeAddInput placeholder="Type any country, e.g. France" value={countryInput} onChange={setCountryInput} onKeyDown={handleCountryKeyDown} onAdd={() => addCountry()} />

            {/* Popular Suggestions */}
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">Popular suggestions:</p>
              <div className="flex flex-wrap gap-2">
                {POPULAR_COUNTRIES.filter(c => !data.countries.includes(c)).map(country => (
                  <Badge
                    key={country}
                    variant="outline"
                    className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors px-3 py-1.5 font-normal"
                    onClick={() => toggleCountry(country)}
                  >
                    + {country}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cities (Optional) */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                Specific Cities <span className="text-muted-foreground text-xs">(optional)</span>
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Narrow down to specific cities if needed
            </p>

            {/* Selected Cities Chips */}
            {data.cities.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {data.cities.map(city => (
                  <Badge key={city} variant="default" className="gap-1 pl-2.5 pr-2 py-1.5 bg-success text-success-foreground hover:bg-success/90 border-transparent shadow-sm">
                    <Check className="w-3 h-3" />{city}
                    <X
                      className="w-3 h-3 cursor-pointer hover:opacity-70"
                      onClick={() => removeCity(city)}
                    />
                  </Badge>
                ))}
              </div>
            )}

            <TypeAddInput placeholder="Type any city, e.g. Chennai" value={cityInput} onChange={setCityInput} onKeyDown={handleCityKeyDown} onAdd={() => addCity()} />

            {/* City Suggestions based on Country */}
            {suggestedCities.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-2">Suggested cities:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedCities.slice(0, 10).map(city => (
                    <Badge
                      key={city}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-colors px-3 py-1.5 font-normal"
                      onClick={() => addCity(city)}
                    >
                      + {city}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
